import { useMemo } from 'react';
import { normalizePersonName, type CrewMember } from '@ast-data/shared';
import { useUsersActiveFirst } from '../users/useActiveUsers';
import { useUserLabel } from '../users/useUserLabel';
import { useAccountResolver } from '../users/personUserId';

/**
 * Resolves a person to a stable crew key. Prefers the app-user id — a stored
 * `*Id` on the record, else the account the app's one name rule places the
 * name on (accountMatching: `K.Hecht` = `K. Hecht` = `Katrin Hecht`) — so the
 * same person collapses to ONE row however their name was typed. A free-text
 * person is keyed by the same rule's normalized name (`n:k hecht`), so their
 * spellings collapse too.
 *
 * Over EVERY account, deactivated ones included: this reads what is already
 * recorded rather than offering a choice. Left out, somebody who has since left
 * would split into an account row and a free-text row — the same person twice.
 */
export interface CrewKeyResolver {
    /** Canonical key for a recorded person (id wins over name). */
    keyForName(name: string, storedId?: string): string;
    /** Canonical key for a saved crew member (keep an already-id key). */
    keyForMember(member: Pick<CrewMember, 'personKey' | 'name'>): string;
    /** Display name for a key: the user's current label when id-linked, else name. */
    displayName(name: string, key: string): string;
}

/** The key of a person no account fits: their name, as the name rule reads it. */
function freeTextKey(name: string): string {
    const normalized = normalizePersonName(name);
    return normalized ? `n:${normalized}` : '';
}

export function useCrewKeyResolver(): CrewKeyResolver {
    // The forms' resolver: the active accounts first, then every account.
    const resolveAccount = useAccountResolver();
    const users = useUsersActiveFirst();
    const nameOf = useUserLabel();
    return useMemo(() => {
        const labelById = new Map<string, string>();
        for (const user of users) {
            labelById.set(user.id, nameOf(user));
        }
        const keyForName = (name: string, storedId?: string): string => {
            const id =
                (storedId || '').trim() || resolveAccount(name)?.id || '';
            return id ? `u:${id}` : freeTextKey(name);
        };
        return {
            keyForName,
            keyForMember: (member) =>
                member.personKey.startsWith('u:')
                    ? member.personKey
                    : keyForName(member.name),
            displayName: (name, key) => {
                if (key.startsWith('u:')) {
                    const label = labelById.get(key.slice(2));
                    if (label) {
                        return label;
                    }
                }
                return name;
            }
        };
    }, [resolveAccount, users, nameOf]);
}
